/**
 * TaskProjectTags Component
 *
 * Renders the projects a task is assigned to as chips, primary first. Past the first two the
 * rest collapse into a "+N" chip — hovering lists the hidden ones, clicking opens a modal with
 * the full list. Callers pass their own chip classes so the drawer and the details page keep
 * their existing look.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { getTaskProjects } from '../../utils/task.utils';

const VISIBLE_TAGS = 2;

const projectPath = (id) => `/tasks/projects/details/${id}`;

const TaskProjectTags = ({
  task,
  tagClassName,
  linkClassName,
  iconClassName,
  labelFirst,
}) => {
  const [showAll, setShowAll] = useState(false);

  const projects = getTaskProjects(task);
  if (!projects.length) return null;

  const visible = projects.slice(0, VISIBLE_TAGS);
  const hidden = projects.slice(VISIBLE_TAGS);

  return (
    <>
      <div className="task-project-tags">
        {visible.map((project, index) => (
          <Link
            key={project.id}
            to={projectPath(project.id)}
            aria-label={`View project: ${project.name}`}
            className={`task-project-tags__link ${linkClassName || ''}`}
          >
            <span
              className={`${tagClassName} ${
                index === 0
                  ? 'task-project-tags__tag--primary'
                  : 'task-project-tags__tag--secondary'
              }`}
            >
              <LinkOutlined className={iconClassName} aria-hidden="true" />
              {labelFirst && index === 0 ? `Project: ${project.name}` : project.name}
            </span>
          </Link>
        ))}

        {hidden.length > 0 && (
          <Tooltip title={hidden.map((project) => project.name).join(', ')}>
            <span
              className={`${tagClassName} task-project-tags__tag--secondary task-project-tags__more`}
              role="button"
              tabIndex={0}
              aria-label={`Show all ${projects.length} projects`}
              onClick={() => setShowAll(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setShowAll(true);
                }
              }}
            >
              +{hidden.length}
            </span>
          </Tooltip>
        )}
      </div>

      <Modal
        title={`Projects (${projects.length})`}
        visible={showAll}
        onCancel={() => setShowAll(false)}
        footer={null}
        className="task-project-tags__modal"
      >
        {projects.map((project, index) => (
          <Link
            key={project.id}
            to={projectPath(project.id)}
            onClick={() => setShowAll(false)}
            className="task-project-tags__modal-item"
          >
            <LinkOutlined aria-hidden="true" />
            <span className="task-project-tags__modal-name">{project.name}</span>
            {index === 0 && <span className="task-project-tags__modal-primary">Primary</span>}
          </Link>
        ))}
      </Modal>
    </>
  );
};

TaskProjectTags.propTypes = {
  task: PropTypes.shape({
    project: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    projects: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      })
    ),
  }),
  tagClassName: PropTypes.string.isRequired,
  linkClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  labelFirst: PropTypes.bool,
};

TaskProjectTags.defaultProps = {
  task: null,
  linkClassName: '',
  iconClassName: '',
  labelFirst: false,
};

export default TaskProjectTags;
